import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import Sidebar from "../../sidebar";
import Header from "../../Header";
import Footer from "../../footer";
import useToggleSidebar from "../../../components/togglesidebar";
import api from "../../../services/api";
import {
  formatMoney,
  getCachedSiteCurrency,
  setCachedSiteCurrency,
} from "../../../lib/money";

function Settings() {
  const { isSidebarOpen, toggleSidebar } = useToggleSidebar();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [allowed, setAllowed] = useState([]);
  const [currency, setCurrency] = useState(() => getCachedSiteCurrency());

  const role = useMemo(() => {
    try {
      const raw = localStorage.getItem("adminUser");
      if (!raw) return "";
      const parsed = JSON.parse(raw);
      return String(parsed?.role || "").toLowerCase();
    } catch {
      return "";
    }
  }, []);

  const canEdit = role === "superadmin";

  const fetchCurrency = async () => {
    try {
      return await api.get("/settings/currency");
    } catch (err) {
      if (err?.response?.status === 404) {
        return await api.get("/site-settings/currency");
      }
      throw err;
    }
  };

  const fetchAllowed = async () => {
    try {
      return await api.get("/settings/currency/allowed");
    } catch (err) {
      if (err?.response?.status === 404) {
        return await api.get("/site-settings/currency/allowed");
      }
      throw err;
    }
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError("");
      setSuccess("");

      try {
        const [currencyRes, allowedRes] = await Promise.all([
          fetchCurrency(),
          fetchAllowed(),
        ]);

        const c = currencyRes?.data?.currency;
        const nextCurrency = {
          code: String(c?.code || "INR").trim().toUpperCase(),
          locale: String(c?.locale || "en-IN").trim(),
        };

        const allowedList = Array.isArray(allowedRes?.data?.allowed)
          ? allowedRes.data.allowed
          : [];

        let normalizedAllowed = allowedList
          .map((x) => String(x).trim().toUpperCase())
          .filter(Boolean);

        if (normalizedAllowed.length === 0) {
          try {
            if (typeof Intl.supportedValuesOf === "function") {
              const list = Intl.supportedValuesOf("currency");
              if (Array.isArray(list) && list.length > 0) {
                normalizedAllowed = list.map((x) => String(x).trim().toUpperCase()).filter(Boolean);
              }
            }
          } catch {
          }
        }

        if (cancelled) return;

        setCurrency(nextCurrency);
        setCachedSiteCurrency(nextCurrency);
        setAllowed(normalizedAllowed);
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 401) {
          setError("Unauthorized. Please log in again.");
        } else if (status === 404) {
          setError(
            "Settings API not found (404). Please restart the backend running on port 5000."
          );
        } else {
          setError("Unable to load settings.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const onSave = async () => {
    if (!canEdit) return;

    const nextCode = String(currency.code || "").trim().toUpperCase();
    if (nextCode.length !== 3) {
      setError("Please enter a valid 3-letter currency code.");
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");

    try {
      let res;
      try {
        res = await api.put("/settings/currency", {
          code: nextCode,
        });
      } catch (err) {
        if (err?.response?.status === 404) {
          res = await api.put("/site-settings/currency", {
            code: nextCode,
          });
        } else {
          throw err;
        }
      }

      const c = res?.data?.currency;
      const nextCurrency = {
        code: String(c?.code || currency.code).trim().toUpperCase(),
        locale: String(c?.locale || currency.locale).trim(),
      };

      setCurrency(nextCurrency);
      setCachedSiteCurrency(nextCurrency);
      setSuccess("Currency updated.");
    } catch (err) {
      setError("Unable to update currency.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Admin | Settings</title>
      </Helmet>

      <Sidebar isSidebarOpen={isSidebarOpen} />

      <div
        className={`content-overlay ${isSidebarOpen && window.innerWidth <= 992 ? "blurred" : ""}`}
        onClick={() =>
          isSidebarOpen && window.innerWidth <= 992 && toggleSidebar()
        }
      >
        <div
          className="content-wrapper"
          style={{
            marginLeft: isSidebarOpen ? "250px" : "70px",
            transition: "margin-left .3s",
          }}
        >
          <Header toggleSidebar={toggleSidebar} />

          <section className="content">
            <div className="container-fluid">
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <h4 className="mb-0">Settings</h4>
                    </div>

                    <div className="card-body">
                      {loading ? (
                        <div>Loading...</div>
                      ) : (
                        <>
                          {error && <div className="alert alert-danger">{error}</div>}
                          {success && <div className="alert alert-success">{success}</div>}

                          <div className="row">
                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label">Site Currency</label>
                                <input
                                  className="form-control"
                                  value={currency.code}
                                  list="currency-codes"
                                  onChange={(e) =>
                                    setCurrency((prev) => ({
                                      ...prev,
                                      code: String(e.target.value || "")
                                        .trim()
                                        .toUpperCase()
                                        .slice(0, 3),
                                    }))
                                  }
                                  disabled={!canEdit || saving}
                                />
                                {allowed.length > 0 && (
                                  <datalist id="currency-codes">
                                    {allowed.map((code) => (
                                      <option key={code} value={code} />
                                    ))}
                                  </datalist>
                                )}
                                {!canEdit && (
                                  <div className="form-text">
                                    Only superadmin can change currency.
                                  </div>
                                )}
                              </div>

                              <div className="mb-3">
                                <label className="form-label">Locale</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={currency.locale}
                                  readOnly
                                />
                              </div>

                              <button
                                type="button"
                                className="btn btn-primary"
                                onClick={onSave}
                                disabled={!canEdit || saving || loading}
                              >
                                {saving ? "Saving..." : "Save"}
                              </button>
                            </div>

                            <div className="col-md-6">
                              <div className="mb-3">
                                <label className="form-label">Preview</label>
                                <div className="border rounded p-3">
                                  <div className="text-muted">Example price</div>
                                  <div className="h5 mb-0">{formatMoney(1234.56, currency)}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <Footer />
        </div>
      </div>
    </>
  );
}

export default Settings;
